import { useState } from 'react'
import { message } from 'antd'
import { assistantInitiate } from '../../../../../../AI Tools/utils'
import { generateAIResponse } from '../../../../../../AI Tools/assistants-lib'
import { AssistantEnums } from '../../../../../../AI Tools/assitants-enums'
import { createClinicDataRecords } from '../../../../../examination-utils/examination-lib'
import dayjs from 'dayjs'

const useDataExtraction = ({ userRef, bookingData, agiliteUser, websocketConnection, onRefresh }) => {
  const [isExtracting, setIsExtracting] = useState(false)
  const [extractedVitals, setExtractedVitals] = useState([])
  const [selectedVitals, setSelectedVitals] = useState({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Map AI response keys to database keys
  const mapVitalKey = (aiKey) => {
    const keyMapping = {
      blood_pressure: 'bloodPressure',
      heart_rate: 'pulse',
      temperature: 'temperature',
      oxygen_saturation: 'oxygenSaturation',
      respiratory_rate: 'respiratoryRate'
    }
    return keyMapping[aiKey] || aiKey
  }

  // Trigger cross-tab vitals update
  const triggerVitalsUpdate = (submittedData) => {
    // Method 1: Custom event for same-window communication
    window.dispatchEvent(
      new CustomEvent('vitals-updated', {
        detail: {
          bookingRef: bookingData?._id,
          data: submittedData,
          source: 'ai_extraction'
        }
      })
    )

    // Method 2: LocalStorage event for cross-tab communication
    const updateData = {
      timestamp: Date.now(),
      bookingRef: bookingData?._id,
      data: submittedData,
      source: 'ai_extraction'
    }

    localStorage.setItem('vitals-update-trigger', JSON.stringify(updateData))
    // Remove after brief moment to allow other tabs to detect the change
    setTimeout(() => {
      localStorage.removeItem('vitals-update-trigger')
    }, 100)
  }

  // Extract vitals from clinical note content
  const extractVitalsFromNote = async (noteContent) => {
    if (!noteContent || !noteContent.trim()) {
      message.error('No content available for data extraction')
      return
    }

    setIsExtracting(true)
    setExtractedVitals([])
    setSelectedVitals({})

    try {
      // Send content to Data Extraction AI
      const tmpData = await assistantInitiate(noteContent, AssistantEnums.assistant_ids.DATA_EXTRACTION_AI)
      const aiResponse = await generateAIResponse(tmpData)
      const aiContent = aiResponse?.data?.data[0]?.content[0]?.text?.value

      if (!aiContent) {
        throw new Error('Failed to get response from Data Extraction AI')
      }

      // Parse the JSON response
      let parsedData
      try {
        parsedData = JSON.parse(aiContent)
      } catch (parseError) {
        console.error('Failed to parse AI response:', aiContent)
        throw new Error('AI returned invalid JSON format')
      }

      if (!parsedData.extractedVitals || !Array.isArray(parsedData.extractedVitals)) {
        throw new Error('AI response missing extractedVitals array')
      }

      if (parsedData.extractedVitals.length === 0) {
        message.info('No vital signs detected in the clinical note')
        setIsExtracting(false)
        return
      }

      // Map the keys to match the database schema
      const mappedVitals = parsedData.extractedVitals.map((vital) => ({
        ...vital,
        key: mapVitalKey(vital.key), // Map to correct database key
        originalKey: vital.key // Keep original for reference
      }))

      // Set extracted vitals and open modal
      setExtractedVitals(mappedVitals)

      // Pre-select all vitals with high confidence (>= 0.8)
      const preSelected = {}
      mappedVitals.forEach((vital, index) => {
        if (vital.confidence >= 0.8) {
          preSelected[index] = true
        }
      })
      setSelectedVitals(preSelected)

      setIsModalOpen(true)
      message.success(`${mappedVitals.length} vital signs extracted from note`)
    } catch (error) {
      console.error('Error extracting vitals:', error)
      message.error(error.message || 'Failed to extract vitals from clinical note')
    } finally {
      setIsExtracting(false)
    }
  }

  // Toggle vital selection
  const toggleVitalSelection = (index) => {
    setSelectedVitals((prev) => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  // Submit selected vitals to database
  const submitSelectedVitals = async () => {
    const selectedIndices = Object.keys(selectedVitals).filter((index) => selectedVitals[index])

    if (selectedIndices.length === 0) {
      message.error('Please select at least one vital sign to submit')
      return
    }

    setIsSubmitting(true)

    try {
      const timestamp = dayjs()
      const vitalsToSubmit = selectedIndices.map((index) => extractedVitals[parseInt(index)])

      // Create data records for each selected vital
      const dataRecordPromises = vitalsToSubmit.map((vital) => {
        const baseData = {
          dateCreated: timestamp,
          createdBy: agiliteUser?._id,
          userRef: bookingData?.userRef || userRef,
          bookingRef: bookingData?._id || '',
          origin: 'ai_extraction',
          category: vital.category,
          key: vital.key // This now uses the mapped key
        }

        // Handle complex values (like blood pressure)
        if (vital.value && typeof vital.value === 'object' && !Array.isArray(vital.value)) {
          const subFields = Object.entries(vital.value).map(([subKey, subValue]) => ({
            key: subKey,
            value: subValue
          }))
          return createClinicDataRecords({
            ...baseData,
            value: subFields
          })
        }

        // Handle simple values
        return createClinicDataRecords({
          ...baseData,
          value: vital.value
        })
      })

      const submittedData = await Promise.all(dataRecordPromises)

      // Send via websocket if available (for Clinical Notes tab)
      if (bookingData?._id && websocketConnection?.isConnected) {
        websocketConnection.socket.send(
          JSON.stringify({
            bookingRef: bookingData._id,
            data: submittedData
          })
        )
      } else if (onRefresh) {
        onRefresh()
      }

      // Trigger cross-tab vitals update (for Assessment tab)
      triggerVitalsUpdate(submittedData)

      message.success(`${selectedIndices.length} vital signs submitted successfully`)
      setIsModalOpen(false)
      setExtractedVitals([])
      setSelectedVitals({})
    } catch (error) {
      console.error('Error submitting vitals:', error)
      message.error('Failed to submit vital signs to database')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Close modal and reset state
  const closeModal = () => {
    setIsModalOpen(false)
    setExtractedVitals([])
    setSelectedVitals({})
  }

  return {
    // State
    isExtracting,
    extractedVitals,
    selectedVitals,
    isModalOpen,
    isSubmitting,

    // Actions
    extractVitalsFromNote,
    toggleVitalSelection,
    submitSelectedVitals,
    closeModal
  }
}

export default useDataExtraction
